import Head from "next/head";
import Link from "next/link";
import COMMONCONSTANT from "@constants/commonConstant";

const Privacy = () => {
  return (
    <>
      <Head>
        <title>Privacy Policy | LoneStar CR</title>
        <meta name="description" content="LoneStar CR Privacy Policy for ClearK12 services." />
        <meta name="robots" content="noindex, follow" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Poppins:wght@700;800;900&display=swap"
          rel="stylesheet"
        />
        <link href="/static/css/privacy.css" rel="stylesheet" key="privacycss" />
      </Head>

      <header aria-label="Main navigation" className="nav">
        <Link href={COMMONCONSTANT.ROUTEPATH.HOME} aria-label="LoneStar CR home">
          <img alt="LoneStar CR" className="logo" src="/static/imgs/Logo.png" />
        </Link>
        <nav aria-label="Privacy page navigation" className="nav-links">
          <Link href={COMMONCONSTANT.ROUTEPATH.HOME}>Home</Link>
          <Link href="/terms">Terms</Link>
          <a className="nav-btn" href={COMMONCONSTANT.ROUTEPATH.LOGINURL}>
            Login
          </a>
        </nav>
      </header>

      <main>
        <section className="legal-hero">
          <span className="eyebrow">LoneStar CR</span>
          <h1>LoneStar CR Privacy Policy</h1>
          <p className="hero-copy">
            This policy describes how ClearK12 handles personal information when you use LoneStar CR and related
            services.
          </p>
        </section>

        <div className="legal-layout">
          <aside className="legal-aside" aria-label="Privacy summary and contact">
            <p className="aside-title">Last Updated</p>
            <p>July 6, 2026</p>
            <div className="contact-card">
              <strong>Questions about privacy?</strong>
              <address>
                ClearK12, Inc.
                <br />
                7800 Dallas Parkway,
                <br />
                Suite 430
                <br />
                Plano, TX 75024
                <br />
                <br />
                <a href="mailto:hi@cleark12.com">hi@cleark12.com</a>
              </address>
            </div>
          </aside>

          <article className="legal-document">
            <p className="effective-date">
              <strong>Last updated:</strong> July 6, 2026
            </p>

            <section className="legal-section">
              <h2>Overview</h2>
              <p>
                This policy describes how we handle your personal information when you use LoneStar CR, a service
                provided by ClearK12, Inc. (&quot;ClearK12&quot;). This Privacy Policy applies to the LoneStar CR
                website, digital messaging (SMS), email, and telephone communications (collectively, &quot;ClearK12
                Services&quot;). In this Privacy Policy, &quot;LoneStar CR,&quot; &quot;ClearK12,&quot;
                &quot;us,&quot; &quot;we,&quot; or &quot;our&quot; refer to ClearK12, Inc.
              </p>
              <p>
                How we handle information about you depends on which ClearK12 Services you use and how you use them.
                If you have questions, please contact us at <a href="mailto:hi@cleark12.com">hi@cleark12.com</a>.
              </p>
            </section>

            <section className="legal-section">
              <h2>1. What Information Do We Gather About You?</h2>
              <p>
                The information we gather about you depends on the context and can personally identify you either on
                its own or when combined with other information. The following describes the information we collect
                and how we obtain it:
              </p>
              <h3>Information Collected Through ClearK12 Services</h3>
              <h3>Information You Voluntarily Give Us</h3>
              <p>
                <strong>For Registration:</strong> When you sign up for ClearK12 Services, we collect personal
                identifiers such as your contact information, including your name and email address, and account
                credentials. We may also ask you to create an account name.
              </p>
              <p>
                <strong>For User-Generated Content:</strong> We may offer you the ability to post content that other
                users can read (e.g., comments or product notes). Anyone can read, collect, and use any personal
                information that accompanies your posts. We do not have to publish any of your content. If the law
                requires us to take down, remove, or edit your content, we will comply.
              </p>
              <p>
                <strong>During Contact with Our Call Center:</strong> We collect information from you when you
                contact us over the phone or through customer service. This can include your name, contact
                information, information about your account, and any other information you choose to provide.
              </p>
            </section>

            <section className="legal-section">
              <h2>2. A Note About Sensitive Personal Information</h2>
              <p>
                We generally do not collect sensitive personal information about you, such as government-issued IDs,
                racial or ethnic origin, political opinions, religious beliefs, citizenship or immigration status, or
                account access information for third-party accounts. However, there may be situations where we
                request certain of the above information. You will always have the option to decline to answer or
                participate in such surveys, panels, or other feedback programs. Outside these situations, we prefer
                you never share sensitive personal information with us.
              </p>
            </section>

            <section className="legal-section">
              <h2>3. Information Collected Automatically</h2>
              <p>
                When you use ClearK12 Services, we collect some information automatically, such as your IP address,
                cookie identifiers, device identifiers, and geolocation information. This information is used to
                personalize content provided to you and to improve ClearK12 Services. For more information about how
                to manage tracking methods on ClearK12 Services, read our Cookie Policy and the &quot;What Are Your
                Rights and Choices?&quot; section below
              </p>
            </section>

            <section className="legal-section">
              <h2>4. What Do We Do with the Information We Collect About You?</h2>
              <p>
                We use each of the categories of personal information described above to operate, improve,
                understand, and personalize ClearK12 Services. This includes providing you with access to ClearK12
                Services, customer service, responding to inquiries, completing transactions, and sending
                service-related transactional messages.
              </p>
            </section>

            <section className="legal-section">
              <h2>5. We Provide ClearK12 Services</h2>
              <p>We use your information to help you use and navigate ClearK12 Services, such as:</p>
              <ul>
                <li>Making ClearK12 Services available to you or fulfilling the purposes for which you provided the information to us</li>
                <li>Arranging access to your account</li>
                <li>Providing customer service</li>
                <li>Responding to inquiries, requests, suggestions, or complaints</li>
                <li>Completing your payments and transactions</li>
                <li>Sending service-related transactional messages and otherwise communicating with you about ClearK12 Services</li>
                <li>Analyzing data on user subscriptions, purchases, registered account activity, and other usage behaviors to improve and develop ClearK12 Services</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>6. We Carry Out Administrative or Legal Tasks</h2>
              <p>
                We process information for auditing, legal, or regulatory purposes, and to detect and prevent fraud
                and security incidents. In the event of a reorganization, merger, sale, joint venture, assignment,
                transfer, or other disposition of all or any portion of our business or assets, we may disclose or
                transfer your information to a third party. We may also preserve or disclose personal information if
                required by law or necessary to protect our rights or the safety of our personnel, users, or the
                public.
              </p>
            </section>

            <section className="legal-section">
              <h2>7. What Are Your Rights and Choices?</h2>
              <p>
                We provide a variety of ways for you to control the personal information we hold about you,
                including choices about how we use that information. These controls and choices may be enforceable
                as rights under applicable law.
              </p>
            </section>

            <section className="legal-section">
              <h2>8. In General</h2>
              <p>
                <strong>Email:</strong> You can unsubscribe from emails and email newsletters by following the
                instructions near the bottom of the email.
              </p>
              <p>
                <strong>Mail or Telephone Promotions:</strong> You can unsubscribe from mail or telephone
                solicitations by emailing us at <a href="mailto:hi@cleark12.com">hi@cleark12.com</a> with &quot;Opt
                Out&quot; in the subject line and your ClearK12 account number and phone number in the body of the
                email.
              </p>
              <p>
                <strong>Text Messages:</strong> You can opt out of text alerts anytime by replying &quot;STOP&quot;
                or any alternative keyword we&apos;ve shared with you.
              </p>
              <p>
                We complete any opt-out request as quickly as we can. This opt-out request won&apos;t prohibit us
                from sending you important non-marketing notices.
              </p>
            </section>

            <section className="legal-section">
              <h2>9. Browser and Platform Controls</h2>
              <p>
                <strong>Cookie Policy:</strong> Most web browsers are set to accept cookies by default. If you
                prefer, you can go to your browser settings to learn how to delete or reject cookies. If you choose
                to delete or reject cookies, this could affect certain features or services of our websites. If you
                choose to delete cookies, settings and preferences controlled by those cookies may be deleted and may
                need to be recreated.
              </p>
              <p>
                <strong>Delete My Account and User Data:</strong> You can delete your registered user account and
                user data by emailing us at <a href="mailto:hi@cleark12.com">hi@cleark12.com</a>. If you delete your
                registered user account, we will remove you from our registered user database and email marketing
                and newsletter lists.
              </p>
            </section>

            <section className="legal-section">
              <h2>10. Other Rights and Choices</h2>
              <p>
                You will not receive discriminatory treatment by us for the exercise of your privacy rights. ClearK12
                does not &quot;sell&quot; your personal information.
              </p>
            </section>

            <section className="legal-section">
              <h2>11. How Long Do You Retain Data?</h2>
              <p>
                We store your personal information for as long as needed or permitted based on the reason why we
                obtained it. This means we might retain your personal information even after you close your account
                with us. When deciding how long to keep your information, we consider the length of our relationship
                with you, legal obligations, and any legal positions we have taken.
              </p>
            </section>

            <section className="legal-section">
              <h2>12. How Do You Protect My Information?</h2>
              <p>
                We protect your personal information with appropriate organizational, technological, and physical
                safeguards but cannot guarantee its absolute security. We recommend using complex and unique
                passwords for your ClearK12 accounts and not sharing your password with anyone. If you believe your
                interaction with us is no longer secure, notify us immediately.
              </p>
            </section>

            <section className="legal-section">
              <h2>13. Are There Guidelines for Children?</h2>
              <p>
                We do not knowingly gather personal information from children under 18 years of age in a manner not
                permitted by the U.S. Children&apos;s Privacy Protection Act (COPPA). If you believe we have
                collected information from your child in a manner not permitted by law, contact us at{" "}
                <a href="mailto:hi@cleark12.com">hi@cleark12.com</a>. We will remove the data as required by
                applicable laws.
              </p>
            </section>

            <section className="legal-section">
              <h2>14. What About Third-Party Services?</h2>
              <p>
                ClearK12 Services may include links to third-party websites, resources, vendors, and advertisers. We
                do not control third-party content or privacy practices. These third parties may independently
                collect information about you and affect your data preferences. Any personal information you provide
                to third parties is not covered by this Privacy Policy. We encourage you to familiarize yourself with
                their privacy policies and terms of use.
              </p>
            </section>

            <section className="legal-section">
              <h2>15. How Are Changes to This Privacy Policy Communicated?</h2>
              <p>
                We periodically update this Privacy Policy to reflect changes in our practices or applicable laws.
                We will post any changes on this page by updating this policy. If we make a significant change in how
                we collect, use, or share your personal information, we will notify you at least 30 days prior to
                the changes taking effect. Your continued use of ClearK12 Services after we post any changes is
                subject to the updated Privacy Policy.
              </p>
            </section>

            <section className="legal-section">
              <h2>16. How Can You Contact Us? Who Is the Controller of Your Personal Information?</h2>
              <p>
                If you have any questions, email us at <a href="mailto:hi@cleark12.com">hi@cleark12.com</a>, or write
                to us at:
              </p>
              <p>
                <br />
                ClearK12, Inc.
                <br />
                7800 Dallas Parkway
                <br />
                Suite 430
                <br />
                Plano TX, 75024
              </p>
            </section>
          </article>
        </div>
      </main>

      <footer aria-label="Footer" className="site-footer">
        <div className="site-footer-inner">
          <div className="footer-brand">
            <img alt="LoneStar CR" className="footer-logo" src="/static/imgs/Logo.png" />
            <div className="footer-copy">&copy; 2026 ClearK12, Inc. All rights reserved.</div>
          </div>
          <nav aria-label="Legal links" className="footer-links">
            <Link href="/terms">Terms &amp; Conditions</Link>
            <span aria-hidden="true" className="footer-divider"></span>
            <Link href="/privacy" aria-current="page">
              Privacy Policy
            </Link>
          </nav>
        </div>
      </footer>
    </>
  );
};

export default Privacy;
